import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Trash2, Upload, Video } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = ["Alpini & Bosnia", "Volontariato in Croce Rossa", "Vodafone & Spagna", "Sanità & YUMA", "Innovazione", "Eventi & Network", "Soluzione Albania", "Stampa & Articoli", "Istantanee"];

export const AdminTools = () => {
  const [photo, setPhoto] = useState({ title: "", text: "", cat: "Istantanee", year: "" });
  const [photoFile, setPhotoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [myPhotos, setMyPhotos] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoInfo, setVideoInfo] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const loadPhotos = () => {
    axios.get(`${API}/gallery`).then((res) => setMyPhotos(res.data)).catch(() => {});
  };
  const loadVideo = () => {
    axios.get(`${API}/site/video`).then((res) => setVideoInfo(res.data)).catch(() => setVideoInfo(null));
  };

  useEffect(() => {
    loadPhotos();
    loadVideo();
  }, []);

  const uploadPhoto = async (e) => {
    e.preventDefault();
    if (!photoFile) {
      toast.error("Scegli un'immagine prima di pubblicare");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", photoFile);
      fd.append("title", photo.title);
      fd.append("text", photo.text);
      fd.append("cat", photo.cat);
      fd.append("year", photo.year);
      await axios.post(`${API}/admin/gallery`, fd, { withCredentials: true });
      toast.success("Foto pubblicata nella Galleria dei Ricordi!");
      setPhoto({ title: "", text: "", cat: "Istantanee", year: "" });
      setPhotoFile(null);
      e.target.reset();
      loadPhotos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload non riuscito");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (id) => {
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { withCredentials: true });
      toast.success("Foto rimossa dalla galleria");
      loadPhotos();
    } catch {
      toast.error("Rimozione non riuscita");
    }
  };

  const uploadVideo = async (e) => {
    e.preventDefault();
    if (!videoFile) {
      toast.error("Scegli un file video (mp4)");
      return;
    }
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", videoFile);
      const res = await axios.post(`${API}/admin/video`, fd, { withCredentials: true });
      setVideoInfo(res.data);
      toast.success("Video pubblicato nella home!");
      setVideoFile(null);
      e.target.reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload video non riuscito");
    } finally {
      setUploadingVideo(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-[#DCEEF5] bg-[#F8FBFD] px-4 py-3 text-sm text-[#0B2530] outline-none focus:border-[#06B6D4] focus:ring-2 focus:ring-cyan-100 transition";
  const labelCls = "block text-xs font-mono-ed uppercase tracking-[0.16em] text-[#5F7E8A] mb-2";

  return (
    <div className="mt-16 space-y-12" data-testid="admin-tools">
      <section>
        <h2 className="font-display text-xl font-bold text-[#0B2530] mb-5 flex items-center gap-2">
          <ImagePlus size={19} className="text-[#06B6D4]" /> Aggiungi una foto alla Galleria
        </h2>
        <form onSubmit={uploadPhoto} className="rounded-3xl bg-white border border-[#DCEEF5] p-7 sm:p-8 space-y-5" data-testid="photo-upload-form">
          <div>
            <label htmlFor="up-file" className={labelCls}>Immagine</label>
            <input id="up-file" type="file" accept="image/*" required data-testid="photo-file-input"
              onChange={(e) => setPhotoFile(e.target.files[0])}
              className="w-full text-sm text-[#3D5A66] file:mr-4 file:rounded-full file:border-0 file:bg-[#ECFEFF] file:px-5 file:py-2.5 file:text-sm file:font-semibold file:text-[#0284C7] hover:file:bg-[#D9F4FD]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="up-title" className={labelCls}>Titolo</label>
              <input id="up-title" required value={photo.title} data-testid="photo-title-input"
                onChange={(e) => setPhoto({ ...photo, title: e.target.value })} className={inputCls} placeholder="Es. Fiera di Milano" />
            </div>
            <div>
              <label htmlFor="up-year" className={labelCls}>Anno / Periodo</label>
              <input id="up-year" value={photo.year} data-testid="photo-year-input"
                onChange={(e) => setPhoto({ ...photo, year: e.target.value })} className={inputCls} placeholder="Es. 2025" />
            </div>
          </div>
          <div>
            <label htmlFor="up-cat" className={labelCls}>Categoria</label>
            <select id="up-cat" value={photo.cat} data-testid="photo-cat-select"
              onChange={(e) => setPhoto({ ...photo, cat: e.target.value })}
              className={inputCls}>
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="up-text" className={labelCls}>Breve spiegazione</label>
            <textarea id="up-text" rows={3} value={photo.text} data-testid="photo-text-input"
              onChange={(e) => setPhoto({ ...photo, text: e.target.value })} className={`${inputCls} resize-none`}
              placeholder="Due righe che raccontano il momento..." />
          </div>
          <button type="submit" disabled={uploading} data-testid="photo-upload-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#0284C7] text-white font-semibold px-7 py-3 hover:bg-[#075E8A] transition-colors duration-300 active:scale-95 disabled:opacity-60">
            <Upload size={15} /> {uploading ? "Pubblicazione..." : "Pubblica in galleria"}
          </button>
        </form>

        {myPhotos.length > 0 && (
          <div className="mt-6">
            <div className="text-xs font-mono-ed uppercase tracking-[0.16em] text-[#5F7E8A] mb-3">Caricate da te ({myPhotos.length})</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="my-photos-list">
              {myPhotos.map((p) => (
                <div key={p.id} className="relative group rounded-2xl overflow-hidden border border-[#DCEEF5] bg-white">
                  <img src={`${API}/files/${p.storage_path}`} alt={p.title} className="w-full h-28 object-cover object-top" />
                  <button onClick={() => removePhoto(p.id)} data-testid={`photo-delete-${p.id}`} aria-label="Elimina"
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 text-[#C2410C] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity active:scale-95">
                    <Trash2 size={14} />
                  </button>
                  <div className="px-3 py-2 text-xs font-semibold text-[#0B2530] truncate">{p.title}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display text-xl font-bold text-[#0B2530] mb-5 flex items-center gap-2">
          <Video size={19} className="text-[#E8894B]" /> Video di presentazione in home
        </h2>
        <form onSubmit={uploadVideo} className="rounded-3xl bg-white border border-[#DCEEF5] p-7 sm:p-8 space-y-5" data-testid="video-upload-form">
          <p className="text-sm text-[#3D5A66] leading-relaxed">
            Carica il tuo video racconto (MP4, max 200MB): apparirà nella home appena sotto il nastro scorrevole.
            Caricandone uno nuovo sostituisci il precedente.
          </p>
          {videoInfo && (
            <div className="rounded-2xl bg-[#F0F9FF] border border-[#BAE6FD] px-5 py-4 text-sm text-[#0369A1]" data-testid="video-current-info">
              Video attualmente online — caricato il {new Date(videoInfo.updated_at).toLocaleString("it-IT")}
            </div>
          )}
          <div>
            <label htmlFor="up-video" className={labelCls}>File video</label>
            <input id="up-video" type="file" accept="video/mp4,video/webm,video/quicktime" required data-testid="video-file-input"
              onChange={(e) => setVideoFile(e.target.files[0])}
              className="w-full text-sm text-[#3D5A66] file:mr-4 file:rounded-full file:border-0 file:bg-[#FDE7D3] file:px-5 file:py-2.5 file:text-sm file:font-semibold file:text-[#C2410C] hover:file:bg-[#FBD9B8]" />
          </div>
          <button type="submit" disabled={uploadingVideo} data-testid="video-upload-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#0B2530] text-white font-semibold px-7 py-3 hover:bg-[#E8894B] transition-colors duration-300 active:scale-95 disabled:opacity-60">
            <Upload size={15} /> {uploadingVideo ? "Caricamento video..." : "Pubblica il video"}
          </button>
        </form>
      </section>
    </div>
  );
};
